export interface IcsEvent {
  language: string;
  subject: string;
  description: string;
  location: string;
  begin: string | number | Date;
  stop: string | number | Date;
  url?: string | null;
}

interface BuildOptions {
  now?: Date;
  uid?: () => string;
}

const CRLF = '\r\n';
const PRODID = '-//tickethead//events//EN';

const pad = (value: number, length = 2): string =>
  String(value).padStart(length, '0');

/** RFC 5545 TEXT value escaping. */
function escapeText(value: string): string {
  return String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\r|\n/g, '\\n');
}

// Floating time: the browser's local time, no timezone marker.
function formatLocal(date: Date): string {
  return (
    `${pad(date.getFullYear(), 4)}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
    `T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
  );
}

function formatUtc(date: Date): string {
  return (
    `${pad(date.getUTCFullYear(), 4)}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`
  );
}

function toDate(value: string | number | Date, field: string): Date {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid ${field} date for the calendar event`);
  }
  return date;
}

function createUid(): string {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const host =
    typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  return `${id}@${host}`;
}

function eventLines(
  event: IcsEvent,
  stamp: string,
  uid: () => string
): string[] {
  const lines = [
    'BEGIN:VEVENT',
    `UID:${uid()}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${formatLocal(toDate(event.begin, 'start'))}`,
    `DTEND:${formatLocal(toDate(event.stop, 'end'))}`,
    `SUMMARY;LANGUAGE=${event.language}:${escapeText(event.subject)}`,
    `DESCRIPTION:${escapeText(event.description)}`,
    `LOCATION:${escapeText(event.location)}`
  ];
  if (event.url) {
    lines.push(`URL:${event.url}`);
  }
  lines.push('END:VEVENT');
  return lines;
}

/** Builds an iCalendar (RFC 5545) document containing the given events. */
export function buildIcs(
  events: IcsEvent[],
  { now = new Date(), uid = createUid }: BuildOptions = {}
): string {
  const stamp = formatUtc(now);
  const lines = [
    'BEGIN:VCALENDAR',
    `PRODID:${PRODID}`,
    'VERSION:2.0',
    ...events.flatMap((event) => eventLines(event, stamp, uid)),
    'END:VCALENDAR'
  ];
  return lines.join(CRLF) + CRLF;
}

/** Triggers a browser download of `<filename>.ics`. */
export function downloadIcs(filename: string, content: string): void {
  const url = URL.createObjectURL(
    new Blob([content], { type: 'text/calendar;charset=utf-8' })
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.ics`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
